<template>
    <div>
        <ul class="cic-container">
            <li class="cic-header">
                <span class="cic-header-title">{{ $t('Profile Assignment') }}</span>
            </li>
            <li>
                <div style="background-color: #eee; padding: 1rem">
                    <CicTextbox :value="userFilter" :placeholder="$t('Search')" clearable @input="search" />
                </div>
                <table id="assign_profiles_table" class="cic-table">
                    <thead>
                        <tr>
                            <th>{{ $t('User') }}</th>
                            <th v-if="$store.hasRight('VIEW_OBJECT_PROFILES')">{{ $t('Object Profile') }}</th>
                            <th v-if="$store.hasRight('VIEW_USER_PROFILES')">{{ $t('Rights Profile') }}</th>
                            <th
                                v-if="$store.hasRight('ADD_USER') || $store.hasRight('REMOVE_USER')"
                                class="th-image"
                            ></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="user in pageUsers" :key="user.userId">
                            <td>{{ user.name }}</td>
                            <td v-if="$store.hasRight('VIEW_OBJECT_PROFILES')">
                                <CicSelect
                                    :enabled="$store.hasRight('ASSIGN_TO_OBJECT_PROFILES')"
                                    :value="user.objectProfileId"
                                    :options="
                                        [{ value: 0, label: $t('All') }].concat(
                                            objectProfiles.map((profile) => ({
                                                value: profile.profileId,
                                                label: profile.name,
                                            })),
                                        )
                                    "
                                    @input="(val) => assignObjectProfile(user.userId, val)"
                                />
                            </td>
                            <td v-if="$store.hasRight('VIEW_USER_PROFILES')">
                                <CicSelect
                                    :title="hasRightsProfileRights(user) ? '' : $t('LESS_RIGHTS_MESSAGE')"
                                    :enabled="
                                        $store.hasRight('ASSIGN_TO_USER_PROFILES') && hasRightsProfileRights(user)
                                    "
                                    :value="user.rightsProfileId"
                                    :options="
                                        rightsProfiles
                                            .filter(
                                                (profile) =>
                                                    profile.allowed || profile.profileId === user.rightsProfileId,
                                            )
                                            .map((profile) => ({
                                                value: profile.profileId,
                                                label: profile.name,
                                            }))
                                    "
                                    @input="(val) => assignRightsProfile(user.userId, val)"
                                />
                            </td>
                            <td v-if="$store.hasRight('ADD_USER') || $store.hasRight('REMOVE_USER')">
                                <IconButton
                                    v-if="$store.hasRight('REMOVE_USER')"
                                    icon="remove-icon"
                                    @click="removeUser(user.userId)"
                                />
                            </td>
                        </tr>
                        <tr
                            v-if="
                                $store.hasRight('ADD_USER') &&
                                unassigned.length > 0 &&
                                (($store.hasRight('VIEW_USER_PROFILES') &&
                                    $store.hasRight('ASSIGN_TO_USER_PROFILES')) ||
                                    $store.user.ownerId === $store.user.orgId)
                            "
                        >
                            <td>
                                <CicSelect
                                    id="assign_user"
                                    :value="newUserId"
                                    :options="filteredOptions.map((user) => ({ value: user.userId, label: user.name }))"
                                    :remote-method="getOptions"
                                    remote
                                    @input="(val) => (newUserId = val)"
                                />
                            </td>
                            <td v-if="$store.hasRight('VIEW_OBJECT_PROFILES')">
                                <CicSelect
                                    v-if="$store.hasRight('ASSIGN_TO_OBJECT_PROFILES')"
                                    :value="newObjectProfileId"
                                    :options="
                                        [{ value: 0, label: $t('All') }].concat(
                                            objectProfiles.map((profile) => ({
                                                value: profile.profileId,
                                                label: profile.name,
                                            })),
                                        )
                                    "
                                    @input="(val) => (newObjectProfileId = val)"
                                />
                            </td>
                            <td v-if="$store.hasRight('VIEW_USER_PROFILES')">
                                <CicSelect
                                    v-if="$store.hasRight('ASSIGN_TO_USER_PROFILES')"
                                    :value="newProfileId"
                                    :options="
                                        rightsProfiles
                                            .filter((profile) => profile.allowed)
                                            .map((profile) => ({
                                                value: profile.profileId,
                                                label: profile.name,
                                            }))
                                    "
                                    @input="(val) => (newProfileId = val)"
                                />
                            </td>
                            <td>
                                <IconButton icon="add-icon" @click="addUser" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </li>
            <li class="cic-footer">
                <CicPageControl :page="page" :total-rows="filtered.length" @change="(val) => (page = val)" />
            </li>
        </ul>
        <ObjectProfiles
            v-if="$store.hasRight('VIEW_OBJECT_PROFILES')"
            :items="objectProfiles"
            @change="loadObjectProfiles"
        />
        <RightsProfiles
            v-if="$store.hasRight('VIEW_USER_PROFILES')"
            :items="rightsProfiles.filter((profile) => profile.allowed)"
            @change="loadRightsProfiles"
        />
    </div>
</template>

<script>
    import CicPageControl from '@/components/CicPageControl';
    import IconButton from '@/components/controls/IconButton';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import ObjectProfiles from './ObjectProfiles';
    import RightsProfiles from './RightsProfiles';
    import { load, save } from '@/utils/Http';
    import './resources/styles/userProfiles.css';

    export default {
        name: 'UserProfiles',
        components: {
            CicPageControl,
            CicSelect,
            CicTextbox,
            IconButton,
            ObjectProfiles,
            RightsProfiles,
        },
        data() {
            return {
                filteredOptions: [],
                newProfileId: null,
                newObjectProfileId: 0,
                newUserId: null,
                objectProfiles: [],
                page: 1,
                rightsProfiles: [],
                users: [],
                userFilter: '',
            };
        },
        computed: {
            assigned() {
                return this.users.filter((user) => user.rightsProfileId);
            },
            unassigned() {
                return this.users.filter((user) => !user.rightsProfileId);
            },
            filtered() {
                return this.assigned.filter((user) => user.name.toLowerCase().includes(this.userFilter.toLowerCase()));
            },
            pageUsers() {
                return this.filtered.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        created() {
            this.loadObjectProfiles();
            this.loadRightsProfiles();
            this.loadUsers();
        },
        methods: {
            addUser() {
                if (
                    this.$store.hasRight('VIEW_USER_PROFILES') &&
                    this.$store.hasRight('ASSIGN_TO_USER_PROFILES') &&
                    (!this.newUserId || !this.newProfileId)
                ) {
                    alert(this.$t('Please select a profile'));
                    return;
                }

                save(
                    'userprofiles/addassignment',
                    {
                        userId: this.newUserId,
                        profileId: this.newProfileId,
                        objectProfileId: this.newObjectProfileId,
                    },
                    () => {
                        this.page = 1;
                        this.loadUsers();
                    },
                );
            },
            assignObjectProfile(userId, profileId) {
                save('objectprofile/assign', { userId, profileId }, () => {
                    this.loadUsers();
                });
            },
            assignRightsProfile(userId, profileId) {
                save('rightsprofile/assign', { userId, profileId }, () => {
                    this.loadUsers();
                });
            },
            getOptions(query) {
                this.filteredOptions = this.unassigned
                    .filter((user) => query === '' || user.name.toLowerCase().includes(query.toLowerCase()))
                    .slice(0, 100);
            },
            loadObjectProfiles() {
                if (this.$store.hasRight('VIEW_OBJECT_PROFILES')) {
                    load('objectprofiles/get', {}, (response) => {
                        this.objectProfiles = response.profiles;
                    });
                }
            },
            loadRightsProfiles() {
                if (this.$store.hasRight('VIEW_USER_PROFILES')) {
                    load('rightsprofiles/user', {}, (response) => {
                        this.rightsProfiles = response.profiles;
                    });
                }
            },
            loadUsers() {
                this.newUserId = null;
                this.newProfileId = null;
                this.newObjectProfileId = 0;
                load('rightsprofiles/assignments', {}, (response) => {
                    this.users = response.users;
                    this.filteredOptions = this.unassigned.slice(0, 100);
                });
            },
            removeUser(userId) {
                if (confirm(this.$t('Are you sure you want to remove this user from the portal?'))) {
                    save('userprofiles/removeassignment', { userId }, () => {
                        this.page = 1;
                        this.loadUsers();
                    });
                }
            },
            search(text) {
                this.userFilter = text;
                this.page = 1;
            },
            hasRightsProfileRights(user) {
                const profile = this.rightsProfiles.find((profile) => profile.profileId === user.rightsProfileId);
                return !profile || profile.allowed;
            },
        },
    };
</script>
